/**
 * The clocks' band — the hero-banner kit on InOut's cyan, read alike by the
 * dashboard and the public calculator (AC-1). Three things, in this order
 * (owner, 22 Oct 2026 — "clean, direct, not polluted"):
 *
 *   • the answer — how long ONE trip from today can last — once in large
 *     under the hourglass and once in words (`inoutAnswerText`): a heading
 *     that holds one line and the detail under it in a smaller face, as
 *     TestIt's band (owner, 22 Oct 2026 — the heroes' bands alike in height),
 *     never with the days still to confirm in it. The hourglass is the answer's: its
 *     upper bulb what is left of the limit the trip reaches first; the sand
 *     falls only while the person is away (`running`);
 *   • why — the title's limits on absence as meters (`InOutLimitMeters`), the
 *     one the number comes from marked with the answer's hourglass;
 *   • one line of what else to know: the days still to confirm (one number
 *     for the record, `inoutDaysToConfirm`, with the way to settle them where
 *     the page has one) and the stay in the other Schengen states as the
 *     condition it is (`inoutOtherSchengenStay`).
 *
 * A rule with no number shows no number anywhere (AC-2). Every rule in full,
 * trip by trip, is the Limits tab's. The page brings the eyebrow and the
 * band's one action.
 *
 * An InOut of the person's history (`tone: 'closed'`) is read on the same band
 * in no hero's colour — it no longer runs — with the numbers as of the day it
 * was closed (`asOf`, H7): its words say that day where a running InOut's say
 * "today".
 */
import { CircleDot, Info } from '@tonnie/assets';
import {
  inoutAnswer,
  inoutAnswerFigure,
  inoutAnswerText,
  inoutAnswerUnitText,
  inoutDaysToConfirm,
  inoutNoTitleText,
  inoutOtherSchengenStay,
} from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import type { InOutReading } from '@tonnie/sdk';
import { HeroBand, HeroEyebrow, HeroHeading, HeroRingFigure, cn } from '@tonnie/ui';
import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { INOUT_CLASSES } from '../lib/inout-tone';
import { InOutHourglass } from './InOutHourglass';
import { InOutLimitMeters } from './InOutLimitMeters';

interface InOutStatusBandProps {
  reading: InOutReading;
  /** The pills above the title — where the person is, or what is being read. */
  eyebrow: ReactNode;
  /** The band's one action. */
  aside?: ReactNode;
  /** The person is away now — the hourglass's sand falls. The calculator's answer never runs. */
  running?: boolean;
  /**
   * Where the days still to confirm are settled — the dashboard's notices; the
   * calculator has no record to settle them in (null: its own note says how).
   */
  settleHref?: string | null;
  /** `live` — the InOut running (InOut's cyan); `closed` — one of the history, read only. */
  tone?: 'live' | 'closed';
  /** The day the reading is as of when it is not today (a closed InOut, H7); null — today. */
  asOf?: string | null;
}

const SURFACE = {
  live: 'bg-linear-to-br from-banner-inout-from via-banner-inout-via to-banner-inout-to',
  closed: 'bg-linear-to-br from-banner-closed-from via-banner-closed-via to-banner-closed-to',
} as const;

const CHIP =
  'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-semibold ring-1 ring-white/25';

export function InOutStatusBand({
  reading,
  eyebrow,
  aside,
  running = false,
  settleHref = '/inout/notices',
  tone = 'live',
  asOf = null,
}: InOutStatusBandProps) {
  const { t, formatDate, formatNumber } = useLocale();
  const countryName = useCountryName();
  const { evaluation, regimeLabel } = reading;
  const answer = inoutAnswer(evaluation);
  const counted = inoutAnswerFigure(answer);
  const country = countryName(evaluation.country);
  const toConfirm = inoutDaysToConfirm(evaluation);
  const otherSchengen = inoutOtherSchengenStay(evaluation);
  const pastDay = asOf === null ? null : formatDate(asOf, 'calendarDay');
  const { title, subtitle } = inoutAnswerText(evaluation, {
    t,
    formatNumber,
    formatDate: (date) => formatDate(date, 'calendarDay'),
    country,
    pastDay,
  });

  const confirmWords = t('inout.banner.toConfirmChip', {
    count: toConfirm,
    days: formatNumber(toConfirm),
  });

  return (
    <HeroBand
      surfaceClassName={SURFACE[tone]}
      borderClassName={tone === 'live' ? INOUT_CLASSES.line : 'border-tone-slate-line'}
      cardProps={{ 'data-testid': 'inout-banner', 'data-tone': tone }}
      ring={
        <div className="flex flex-col items-center gap-2 text-white">
          <InOutHourglass
            size="band"
            leftPercent={counted ? counted.leftPercent : 0}
            running={running && counted !== null}
            label={
              counted
                ? t('inout.banner.hourglassLabel', {
                    left: formatNumber(counted.days),
                    limit: formatNumber(counted.limit),
                  })
                : t('inout.banner.noNumber')
            }
            testId="inout-banner-hourglass"
          />
          <div className="flex flex-col items-center">
            {counted ? (
              <HeroRingFigure
                value={formatNumber(counted.days)}
                caption={inoutAnswerUnitText(answer, t) ?? undefined}
                testId="inout-banner-remaining"
              />
            ) : (
              <HeroRingFigure
                value="—"
                caption={t('inout.banner.noNumber')}
                testId="inout-banner-remaining"
                short
              />
            )}
          </div>
        </div>
      }
      aside={aside}
    >
      <HeroEyebrow>
        {eyebrow}
        <span className="text-sm font-medium text-white/90" data-testid="inout-banner-title">
          {regimeLabel ?? inoutNoTitleText(t, pastDay)}
        </span>
      </HeroEyebrow>
      <HeroHeading
        as="h2"
        title={title}
        subtitle={subtitle ?? undefined}
        testId="inout-banner-answer"
        subtitleTestId="inout-banner-answer-detail"
      />
      <InOutLimitMeters evaluation={evaluation} />
      {(toConfirm > 0 || otherSchengen) && (
        <p className="mt-4 flex flex-wrap gap-2" data-testid="inout-banner-notes">
          {toConfirm > 0 &&
            (settleHref ? (
              <Link
                href={settleHref}
                className={cn(CHIP, 'bg-warning text-warning-foreground ring-0 hover:opacity-90')}
                data-testid="inout-banner-to-confirm"
              >
                <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />
                {confirmWords}
              </Link>
            ) : (
              <span
                className={cn(CHIP, 'bg-warning text-warning-foreground ring-0')}
                data-testid="inout-banner-to-confirm"
              >
                <CircleDot className="h-3.5 w-3.5" aria-hidden="true" />
                {confirmWords}
              </span>
            ))}
          {otherSchengen && (
            <span
              className={cn(CHIP, 'bg-white/10 text-white')}
              data-testid="inout-banner-other-schengen"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
              {t('inout.banner.otherSchengen', {
                count: otherSchengen.days,
                days: formatNumber(otherSchengen.days),
              })}
            </span>
          )}
        </p>
      )}
    </HeroBand>
  );
}
